import React, { useEffect, useState, useCallback } from 'react';
import { adminFetch } from '../../lib/api';
import {
  Link, CheckCircle, XCircle, DollarSign, TrendingUp, Users, Clock, Euro,
  Settings, Copy, RefreshCw, Plus, Gift, Star, Zap, Percent, Shield, Eye,
  ChevronDown, ExternalLink, Hash, Save, ToggleLeft, ToggleRight, Loader2,
} from 'lucide-react';

// ═══════════════════════════════════════════════════════════════
//  TYPES & CONSTANTS
// ═══════════════════════════════════════════════════════════════

interface AffiliateStats {
  activeAffiliates: number;
  totalConversions: number;
  revenue30d: number;
  pendingCommissions: number;
}

interface AffiliatePartner {
  id: string;
  name: string;
  type: string;
  status: string;
  referral_code: string;
  copecart_affiliate_id: string;
  commission_model: string;
  commission_rate: number;
  contact_person: string;
  contact_email: string;
  website: string;
  created_at: string;
  stats: {
    totalConversions: number;
    pendingConversions: number;
    totalRevenue: number;
    totalCommission: number;
    paidCommission: number;
  };
}

interface ReferralConfig {
  enabled: boolean;
  doubleSided: boolean;
  referrer: RewardConfig;
  referred: RewardConfig;
  freeMinQuestions: number;
  freeMaxChapters: number;
  freeChaptersPerReferral: number;
}

interface RewardConfig {
  type: string;
  days: number;
  bonus_xp: number;
  discount_percent: number;
}

type Tab = 'overview' | 'partners' | 'conversions' | 'payouts' | 'config';

const CONV_STATUS: Record<string, { label: string; color: string }> = {
  pending: { label: 'Ausstehend', color: '#f59e0b' },
  approved: { label: 'Genehmigt', color: '#3b82f6' },
  paid: { label: 'Bezahlt', color: '#10b981' },
  rejected: { label: 'Abgelehnt', color: '#ef4444' },
  refunded: { label: 'Erstattet', color: '#6b7280' },
};

const REWARD_TYPES: Record<string, { label: string; emoji: string; desc: string }> = {
  premium_extension: { label: 'Premium-Verlängerung', emoji: '⭐', desc: 'Verlängert das Premium-Abo um X Tage' },
  discount: { label: 'Rabatt auf Monatsabo', emoji: '💰', desc: 'Prozentualer Rabatt auf nächstes Abo' },
  bonus_xp: { label: 'Bonus-XP', emoji: '⚡', desc: 'Sofortiger XP-Bonus für den User' },
  combined: { label: 'Kombination', emoji: '🎁', desc: 'Mehrere Belohnungen kombiniert' },
};

const PARTNER_TYPES: Record<string, string> = {
  affiliate: '🔗 Affiliate',
  kooperation: '🤝 Kooperation',
  influencer: '📱 Influencer',
  berater: '🎯 Berater',
  fahrlehrer: '🚗 Fahrlehrer',
};

// ═══════════════════════════════════════════════════════════════
//  MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════

export const SalesAffiliatePage: React.FC = () => {
  const [tab, setTab] = useState<Tab>('overview');
  const [stats, setStats] = useState<AffiliateStats | null>(null);
  const [partners, setPartners] = useState<AffiliatePartner[]>([]);
  const [conversions, setConversions] = useState<any[]>([]);
  const [payouts, setPayouts] = useState<any[]>([]);
  const [referralConfig, setReferralConfig] = useState<ReferralConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [convFilter, setConvFilter] = useState('');
  const [configSaving, setConfigSaving] = useState(false);
  const [configSaved, setConfigSaved] = useState(false);
  const [codeGenerating, setCodeGenerating] = useState(false);
  const [toast, setToast] = useState('');

  const showToast = (msg: string) => { setToast(msg); setTimeout(() => setToast(''), 3000); };

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [sR, pR, cR, payR, cfgR] = await Promise.all([
        adminFetch('/api/admin/sales/affiliates/stats'),
        adminFetch('/api/admin/sales/affiliates/partners'),
        adminFetch('/api/admin/sales/affiliates/conversions'),
        adminFetch('/api/admin/sales/affiliates/payouts'),
        adminFetch('/api/admin/sales/affiliates/referral-config'),
      ]);
      const sData = await sR.json();
      const pData = await pR.json();
      const cData = await cR.json();
      const payData = await payR.json();
      const cfgData = await cfgR.json();
      setStats(sData && sData.activeAffiliates !== undefined ? sData : { activeAffiliates: 0, totalConversions: 0, revenue30d: 0, pendingCommissions: 0 });
      setPartners(Array.isArray(pData) ? pData : []);
      setConversions(Array.isArray(cData) ? cData : []);
      setPayouts(Array.isArray(payData) ? payData : []);
      setReferralConfig(cfgData && cfgData.enabled !== undefined ? cfgData : { enabled: false, doubleSided: true, referrer: { type: 'bonus_xp', days: 0, bonus_xp: 500, discount_percent: 0 }, referred: { type: 'bonus_xp', days: 0, bonus_xp: 200, discount_percent: 0 } });
    } catch (error) {
      console.error(error);
      setStats({ activeAffiliates: 0, totalConversions: 0, revenue30d: 0, pendingCommissions: 0 });
      setPartners([]);
      setConversions([]);
      setPayouts([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  // Actions
  const approveConversion = async (id: string) => {
    await adminFetch(`/api/admin/sales/affiliates/conversions/${id}/approve`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: '{}',
    });
    showToast('Conversion genehmigt');
    loadAll();
  };

  const rejectConversion = async (id: string) => {
    if (!confirm('Conversion ablehnen?')) return;
    await adminFetch(`/api/admin/sales/affiliates/conversions/${id}/reject`, { method: 'PUT' });
    showToast('Conversion abgelehnt');
    loadAll();
  };

  const createPayout = async (partnerId: string) => {
    try {
      await adminFetch('/api/admin/sales/affiliates/payouts', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ partner_id: partnerId }),
      });
      showToast('Auszahlung erstellt');
      loadAll();
    } catch {
      showToast('Keine genehmigten Provisionen vorhanden');
    }
  };

  const markPaid = async (id: string) => {
    const ref = prompt('Zahlungsreferenz (z.B. IBAN-Transfer, PayPal-ID):');
    if (ref === null) return;
    await adminFetch(`/api/admin/sales/affiliates/payouts/${id}/mark-paid`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ payment_reference: ref || '', payment_method: 'bank_transfer' }),
    });
    showToast('Als bezahlt markiert');
    loadAll();
  };

  const generateCode = async (partnerId: string, prefix?: string) => {
    setCodeGenerating(true);
    try {
      const res = await adminFetch('/api/admin/sales/affiliates/generate-code', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ partner_id: partnerId, prefix }),
      });
      const { code } = await res.json();
      showToast(`Code generiert: ${code}`);
      loadAll();
    } catch {
      showToast('Fehler beim Generieren');
    } finally {
      setCodeGenerating(false);
    }
  };

  const saveConfig = async () => {
    if (!referralConfig) return;
    setConfigSaving(true);
    try {
      await adminFetch('/api/admin/sales/affiliates/referral-config', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(referralConfig),
      });
      setConfigSaved(true);
      showToast('Konfiguration gespeichert');
      setTimeout(() => setConfigSaved(false), 2000);
    } catch {
      showToast('Fehler beim Speichern');
    } finally {
      setConfigSaving(false);
    }
  };

  const copyCode = (code: string) => {
    navigator.clipboard.writeText(code);
    showToast(`Code kopiert: ${code}`);
  };

  const filteredConversions = convFilter
    ? conversions.filter(c => c.status === convFilter)
    : conversions;

  const TABS: { key: Tab; label: string; emoji: string }[] = [
    { key: 'overview', label: 'Übersicht', emoji: '📊' },
    { key: 'partners', label: 'Affiliate-Partner', emoji: '🤝' },
    { key: 'conversions', label: 'Conversions', emoji: '💳' },
    { key: 'payouts', label: 'Auszahlungen', emoji: '💸' },
    { key: 'config', label: 'Referral-Programm', emoji: '🎁' },
  ];

  return (
    <div className="page-container">
      <div className="page-header">
        <h1><Link style={{ marginRight: 8, verticalAlign: 'middle' }} size={24} /> Affiliate & Referral</h1>
      </div>

      {/* Tab Navigation */}
      <div style={{
        display: 'flex', gap: 2, marginBottom: 24, borderBottom: '1px solid var(--border)',
        paddingBottom: 0, overflowX: 'auto',
      }}>
        {TABS.map(t => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            style={{
              padding: '10px 16px', fontSize: 13, fontWeight: tab === t.key ? 600 : 400,
              color: tab === t.key ? 'var(--accent-blue)' : 'var(--text-secondary)',
              background: 'transparent', border: 'none', cursor: 'pointer',
              borderBottom: tab === t.key ? '2px solid var(--accent-blue)' : '2px solid transparent',
              transition: 'all 0.15s', whiteSpace: 'nowrap',
            }}
          >
            {t.emoji} {t.label}
            {t.key === 'conversions' && conversions.length > 0 && (
              <span style={{
                marginLeft: 6, padding: '1px 6px', borderRadius: 8, fontSize: 10,
                background: 'var(--accent-blue)', color: '#fff',
              }}>{conversions.length}</span>
            )}
          </button>
        ))}
      </div>

      {/* ═══════ OVERVIEW TAB ═══════ */}
      {tab === 'overview' && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 16, marginBottom: 24 }}>
            {loading ? (
              Array.from({ length: 4 }).map((_, i) => <div key={i} className="card skeleton" style={{ height: 80 }} />)
            ) : stats && (
              <>
                <KpiCard icon={<Users size={18} />} label="Aktive Affiliates" value={stats.activeAffiliates} color="#3b82f6" />
                <KpiCard icon={<TrendingUp size={18} />} label="Conversions gesamt" value={stats.totalConversions} color="#10b981" />
                <KpiCard icon={<Euro size={18} />} label="Umsatz (30 Tage)" value={`${(stats.revenue30d / 100).toLocaleString('de-DE')} €`} color="#8b5cf6" />
                <KpiCard icon={<Clock size={18} />} label="Offene Provisionen" value={`${(stats.pendingCommissions / 100).toLocaleString('de-DE')} €`} color="#f59e0b" />
              </>
            )}
          </div>

          {/* Referral Program Status */}
          {referralConfig && (
            <div className="card" style={{ padding: 20, marginBottom: 24 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                  <h3 style={{ margin: 0 }}>🎁 Referral-Programm</h3>
                  <p style={{ color: 'var(--text-secondary)', fontSize: 13, marginTop: 4 }}>
                    {referralConfig.enabled ? 'Aktiv — Nutzer können andere einladen' : 'Deaktiviert'}
                  </p>
                </div>
                <span style={{
                  padding: '4px 12px', borderRadius: 8, fontSize: 12, fontWeight: 600,
                  background: referralConfig.enabled ? '#10b98120' : '#6b728020',
                  color: referralConfig.enabled ? '#10b981' : '#6b7280',
                }}>{referralConfig.enabled ? '✅ Aktiv' : '⏸️ Inaktiv'}</span>
              </div>
              {referralConfig.enabled && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginTop: 16 }}>
                  <RewardPreview title="Werber erhält" config={referralConfig.referrer} />
                  <RewardPreview title="Geworbener erhält" config={referralConfig.referred} />
                </div>
              )}
            </div>
          )}

          {/* Top Partners */}
          <div className="card" style={{ padding: 20 }}>
            <h3 style={{ marginBottom: 16 }}>🏆 Top-Partner nach Umsatz</h3>
            {partners.length === 0 ? (
              <p style={{ color: 'var(--text-secondary)', textAlign: 'center', padding: 20 }}>Noch keine Partner vorhanden</p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {partners
                  .sort((a, b) => (b.stats?.totalRevenue || 0) - (a.stats?.totalRevenue || 0))
                  .slice(0, 5)
                  .map((p, i) => (
                    <div key={p.id} style={{
                      display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px',
                      background: 'var(--bg-secondary)', borderRadius: 8,
                    }}>
                      <span style={{ fontSize: 18, width: 24, textAlign: 'center' }}>
                        {i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : `${i + 1}.`}
                      </span>
                      <div style={{ flex: 1 }}>
                        <strong style={{ fontSize: 14 }}>{p.name}</strong>
                        {p.referral_code && (
                          <span style={{ marginLeft: 8, fontSize: 11, color: 'var(--text-secondary)', fontFamily: 'monospace' }}>
                            {p.referral_code}
                          </span>
                        )}
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ fontSize: 14, fontWeight: 600 }}>
                          {((p.stats?.totalRevenue || 0) / 100).toLocaleString('de-DE')} €
                        </div>
                        <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
                          {p.stats?.totalConversions || 0} Conversions
                        </div>
                      </div>
                    </div>
                  ))
                }
              </div>
            )}
          </div>
        </>
      )}

      {/* ═══════ PARTNERS TAB ═══════ */}
      {tab === 'partners' && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(380px, 1fr))', gap: 16 }}>
          {loading ? (
            Array.from({ length: 3 }).map((_, i) => <div key={i} className="card skeleton" style={{ height: 220 }} />)
          ) : partners.length === 0 ? (
            <div className="card" style={{ padding: 40, textAlign: 'center', color: 'var(--text-secondary)', gridColumn: '1/-1' }}>
              Noch keine Affiliate-Partner. Erstelle einen unter „Partner & Multiplikatoren".
            </div>
          ) : (
            partners.map(p => (
              <div key={p.id} className="card" style={{ padding: 20 }}>
                {/* Header */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                  <div>
                    <h3 style={{ margin: 0, fontSize: 16 }}>{p.name}</h3>
                    <div style={{ display: 'flex', gap: 8, marginTop: 4, alignItems: 'center' }}>
                      <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                        {PARTNER_TYPES[p.type] || p.type}
                      </span>
                      <span style={{
                        fontSize: 10, padding: '2px 6px', borderRadius: 6, fontWeight: 600,
                        background: p.status === 'active' ? '#10b98120' : '#6b728020',
                        color: p.status === 'active' ? '#10b981' : '#6b7280',
                      }}>{p.status === 'active' ? 'Aktiv' : p.status}</span>
                    </div>
                  </div>
                  {p.commission_model && (
                    <span style={{ fontSize: 11, color: 'var(--text-secondary)', background: 'var(--bg-secondary)', padding: '3px 8px', borderRadius: 6 }}>
                      {p.commission_model === 'percentage' ? `${p.commission_rate}%` : `${p.commission_rate / 100}€ fix`}
                    </span>
                  )}
                </div>

                {/* Referral Code */}
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px',
                  background: 'var(--bg-secondary)', borderRadius: 8, marginBottom: 12,
                }}>
                  <Hash size={14} style={{ color: 'var(--text-secondary)' }} />
                  {p.referral_code ? (
                    <>
                      <code style={{ flex: 1, fontSize: 14, fontWeight: 600, letterSpacing: '0.05em' }}>{p.referral_code}</code>
                      <button className="btn btn-ghost btn-sm" onClick={() => copyCode(p.referral_code)} title="Kopieren">
                        <Copy size={12} />
                      </button>
                    </>
                  ) : (
                    <>
                      <span style={{ flex: 1, fontSize: 12, color: 'var(--text-secondary)' }}>Kein Code zugewiesen</span>
                      <button
                        className="btn btn-ghost btn-sm"
                        onClick={() => generateCode(p.id, p.name.split(' ')[0])}
                        disabled={codeGenerating}
                      >
                        {codeGenerating ? <Loader2 size={12} className="spin" /> : <RefreshCw size={12} />}
                        {' '}Generieren
                      </button>
                    </>
                  )}
                </div>

                {p.copecart_affiliate_id && (
                  <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 8 }}>
                    CopeCart-ID: <code>{p.copecart_affiliate_id}</code>
                  </div>
                )}

                {/* Performance Stats */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginBottom: 12 }}>
                  <StatBox label="Conversions" value={p.stats?.totalConversions ?? 0} />
                  <StatBox label="Umsatz" value={`${((p.stats?.totalRevenue || 0) / 100).toLocaleString('de-DE')}€`} />
                  <StatBox label="Provision" value={`${((p.stats?.totalCommission || 0) / 100).toLocaleString('de-DE')}€`} />
                </div>

                {/* Actions */}
                <div style={{ display: 'flex', gap: 6, borderTop: '1px solid var(--border)', paddingTop: 12 }}>
                  {(p.stats?.pendingConversions || 0) > 0 && (
                    <span style={{ fontSize: 11, color: '#f59e0b', fontWeight: 600, flex: 1, alignSelf: 'center' }}>
                      ⏳ {p.stats.pendingConversions} offene Conversions
                    </span>
                  )}
                  <button className="btn btn-ghost btn-sm" onClick={() => createPayout(p.id)} title="Auszahlung erstellen">
                    <DollarSign size={12} /> Auszahlung
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* ═══════ CONVERSIONS TAB ═══════ */}
      {tab === 'conversions' && (
        <>
          <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
            {['', 'pending', 'approved', 'paid', 'rejected', 'refunded'].map(s => (
              <button
                key={s}
                onClick={() => setConvFilter(s)}
                className="btn btn-ghost btn-sm"
                style={{
                  background: convFilter === s ? 'var(--accent-blue)' : undefined,
                  color: convFilter === s ? '#fff' : undefined,
                }}
              >
                {s === '' ? 'Alle' : CONV_STATUS[s]?.label}
                {s === '' && ` (${conversions.length})`}
                {s && ` (${conversions.filter(c => c.status === s).length})`}
              </button>
            ))}
          </div>
          <div className="card" style={{ overflow: 'auto' }}>
            <table className="admin-table" style={{ width: '100%' }}>
              <thead>
                <tr>
                  <th>Affiliate</th>
                  <th>Käufer</th>
                  <th>Produkt</th>
                  <th>Betrag</th>
                  <th>Provision</th>
                  <th>Status</th>
                  <th>Datum</th>
                  <th>Aktionen</th>
                </tr>
              </thead>
              <tbody>
                {filteredConversions.length === 0 ? (
                  <tr><td colSpan={8} style={{ textAlign: 'center', padding: 32, color: 'var(--text-secondary)' }}>
                    {conversions.length === 0 ? 'Noch keine Conversions erfasst. Diese werden automatisch bei CopeCart-Zahlungen erstellt.' : 'Keine Conversions mit diesem Filter.'}
                  </td></tr>
                ) : (
                  filteredConversions.map(c => (
                    <tr key={c.id}>
                      <td><strong>{c.sales_partners?.name || c.affiliate_id || '–'}</strong></td>
                      <td style={{ fontSize: 12 }}>{c.buyer_email || '–'}</td>
                      <td>{c.product_name || '–'}</td>
                      <td style={{ fontWeight: 600 }}>{(c.amount / 100).toLocaleString('de-DE')} €</td>
                      <td style={{ color: '#10b981', fontWeight: 600 }}>
                        {c.commission_amount ? `${(c.commission_amount / 100).toLocaleString('de-DE')} €` : '–'}
                      </td>
                      <td>
                        <span style={{
                          padding: '3px 10px', borderRadius: 12, fontSize: 12, fontWeight: 600,
                          color: '#fff', background: CONV_STATUS[c.status]?.color || '#666',
                        }}>{CONV_STATUS[c.status]?.label || c.status}</span>
                      </td>
                      <td style={{ fontSize: 12, whiteSpace: 'nowrap' }}>{new Date(c.created_at).toLocaleDateString('de-DE')}</td>
                      <td>
                        {c.status === 'pending' && (
                          <div style={{ display: 'flex', gap: 4 }}>
                            <button className="btn btn-ghost btn-sm" onClick={() => approveConversion(c.id)} title="Genehmigen" style={{ color: '#10b981' }}>
                              <CheckCircle size={14} />
                            </button>
                            <button className="btn btn-ghost btn-sm" onClick={() => rejectConversion(c.id)} title="Ablehnen" style={{ color: '#ef4444' }}>
                              <XCircle size={14} />
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* ═══════ PAYOUTS TAB ═══════ */}
      {tab === 'payouts' && (
        <div className="card" style={{ overflow: 'auto' }}>
          <table className="admin-table" style={{ width: '100%' }}>
            <thead>
              <tr>
                <th>Partner</th>
                <th>Betrag</th>
                <th>Conversions</th>
                <th>Status</th>
                <th>Zahlungsreferenz</th>
                <th>Bezahlt am</th>
                <th>Aktionen</th>
              </tr>
            </thead>
            <tbody>
              {payouts.length === 0 ? (
                <tr><td colSpan={7} style={{ textAlign: 'center', padding: 32, color: 'var(--text-secondary)' }}>
                  Noch keine Auszahlungen. Genehmige Conversions und erstelle dann eine Auszahlung pro Partner.
                </td></tr>
              ) : (
                payouts.map(p => (
                  <tr key={p.id}>
                    <td><strong>{p.sales_partners?.name || '–'}</strong></td>
                    <td style={{ fontWeight: 600 }}>{(p.total_amount / 100).toLocaleString('de-DE')} €</td>
                    <td>{p.conversion_count}</td>
                    <td>
                      <span style={{
                        padding: '3px 10px', borderRadius: 12, fontSize: 12, fontWeight: 600,
                        color: '#fff', background: p.status === 'paid' ? '#10b981' : '#f59e0b',
                      }}>{p.status === 'paid' ? '✅ Bezahlt' : '⏳ Ausstehend'}</span>
                    </td>
                    <td style={{ fontSize: 12 }}>{p.payment_reference || '–'}</td>
                    <td style={{ fontSize: 12 }}>{p.paid_at ? new Date(p.paid_at).toLocaleDateString('de-DE') : '–'}</td>
                    <td>
                      {p.status !== 'paid' && (
                        <button className="btn btn-ghost btn-sm" onClick={() => markPaid(p.id)}>
                          <DollarSign size={14} /> Bezahlt
                        </button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* ═══════ REFERRAL CONFIG TAB ═══════ */}
      {tab === 'config' && referralConfig && (
        <div style={{ maxWidth: 720 }}>
          {/* Master Toggle */}
          <div className="card" style={{ padding: 24, marginBottom: 24, borderRadius: 14 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16 }}>
              <div style={{ flex: 1 }}>
                <h3 style={{ margin: 0, fontSize: 16 }}>Referral-Programm aktivieren</h3>
                <p style={{ color: 'var(--text-secondary)', fontSize: 13, marginTop: 6, lineHeight: 1.5 }}>
                  Wenn aktiv, können App-Nutzer andere zum Beitritt einladen und beide erhalten Belohnungen.
                </p>
              </div>
              <button
                className="btn btn-ghost"
                onClick={() => setReferralConfig({ ...referralConfig, enabled: !referralConfig.enabled })}
                style={{ color: referralConfig.enabled ? '#10b981' : '#6b7280', padding: 8 }}
              >
                {referralConfig.enabled ? <ToggleRight size={32} /> : <ToggleLeft size={32} />}
              </button>
            </div>
          </div>

          {/* Double-sided toggle */}
          <div className="card" style={{ padding: 24, marginBottom: 24, borderRadius: 14 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16 }}>
              <div style={{ flex: 1 }}>
                <h3 style={{ margin: 0, fontSize: 16 }}>Beidseitiges Programm</h3>
                <p style={{ color: 'var(--text-secondary)', fontSize: 13, marginTop: 6, lineHeight: 1.5 }}>
                  Sowohl der Werber als auch der Geworbene erhalten Belohnungen.
                </p>
              </div>
              <button
                className="btn btn-ghost"
                onClick={() => setReferralConfig({ ...referralConfig, doubleSided: !referralConfig.doubleSided })}
                style={{ color: referralConfig.doubleSided ? '#10b981' : '#6b7280', padding: 8 }}
              >
                {referralConfig.doubleSided ? <ToggleRight size={32} /> : <ToggleLeft size={32} />}
              </button>
            </div>
          </div>

          {/* Free User Referral Config */}
          <div className="card" style={{ padding: 24, marginBottom: 24, borderRadius: 14, borderLeft: '4px solid #10b981' }}>
            <h3 style={{ margin: '0 0 8px', fontSize: 16 }}>📚 Free-User Empfehlungsprogramm</h3>
            <p style={{ color: 'var(--text-secondary)', fontSize: 13, marginBottom: 20, lineHeight: 1.5 }}>
              Free User können durch qualifizierte Empfehlungen zusätzliche Kapitel freischalten.
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16 }}>
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: 6 }}>Min. Fragen für Qualifikation</label>
                <input
                  type="number"
                  min={1}
                  max={100}
                  value={referralConfig.freeMinQuestions || 10}
                  onChange={e => setReferralConfig({ ...referralConfig, freeMinQuestions: Number(e.target.value) || 10 })}
                  style={{ width: '100%', padding: '10px 12px', borderRadius: 10, border: '1.5px solid var(--border)', fontSize: 15, fontWeight: 600, textAlign: 'center' }}
                />
                <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 4 }}>Eingeladene müssen X Fragen richtig beantworten</div>
              </div>
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: 6 }}>Kapitel pro Empfehlung</label>
                <input
                  type="number"
                  min={1}
                  max={5}
                  value={referralConfig.freeChaptersPerReferral || 1}
                  onChange={e => setReferralConfig({ ...referralConfig, freeChaptersPerReferral: Number(e.target.value) || 1 })}
                  style={{ width: '100%', padding: '10px 12px', borderRadius: 10, border: '1.5px solid var(--border)', fontSize: 15, fontWeight: 600, textAlign: 'center' }}
                />
                <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 4 }}>Werber erhält X Kapitel pro Qualifikation</div>
              </div>
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: 6 }}>Max. Bonus-Kapitel</label>
                <input
                  type="number"
                  min={1}
                  max={13}
                  value={referralConfig.freeMaxChapters || 5}
                  onChange={e => setReferralConfig({ ...referralConfig, freeMaxChapters: Number(e.target.value) || 5 })}
                  style={{ width: '100%', padding: '10px 12px', borderRadius: 10, border: '1.5px solid var(--border)', fontSize: 15, fontWeight: 600, textAlign: 'center' }}
                />
                <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 4 }}>Insgesamt max. X Kapitel freischaltbar</div>
              </div>
            </div>
          </div>

          {/* Werber Belohnung (Premium) */}
          <div className="card" style={{ padding: 24, marginBottom: 24, borderRadius: 14, borderLeft: '4px solid #3b82f6' }}>
            <h3 style={{ margin: '0 0 20px', fontSize: 16 }}>⭐ Werber erhält</h3>
            <RewardEditor
              config={referralConfig.referrer}
              onChange={c => setReferralConfig({ ...referralConfig, referrer: c })}
            />
          </div>

          {/* Geworbener Belohnung */}
          {referralConfig.doubleSided && (
            <div className="card" style={{ padding: 24, marginBottom: 24, borderRadius: 14, borderLeft: '4px solid #10b981' }}>
              <h3 style={{ margin: '0 0 20px', fontSize: 16 }}>🎁 Geworbener erhält</h3>
              <RewardEditor
                config={referralConfig.referred}
                onChange={c => setReferralConfig({ ...referralConfig, referred: c })}
              />
            </div>
          )}

          {/* Save */}
          <button
            className="btn btn-primary"
            onClick={saveConfig}
            disabled={configSaving}
            style={{ width: '100%', justifyContent: 'center', padding: '14px 0', borderRadius: 12, fontSize: 15, fontWeight: 600 }}
          >
            {configSaving ? <Loader2 size={16} className="spin" /> : configSaved ? <CheckCircle size={16} /> : <Save size={16} />}
            {' '}{configSaving ? 'Speichern...' : configSaved ? 'Gespeichert!' : 'Konfiguration speichern'}
          </button>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div style={{
          position: 'fixed', bottom: 24, right: 24, padding: '12px 20px',
          background: '#1a1a2e', color: '#fff', borderRadius: 10, fontSize: 14,
          boxShadow: '0 8px 32px rgba(0,0,0,0.3)', zIndex: 9999,
          animation: 'fadeIn 0.2s ease',
        }}>
          {toast}
        </div>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════
//  SUB-COMPONENTS
// ═══════════════════════════════════════════════════════════════

const KpiCard: React.FC<{ icon: React.ReactNode; label: string; value: string | number; color: string }> = ({ icon, label, value, color }) => (
  <div className="card" style={{ padding: '16px 14px', display: 'flex', alignItems: 'center', gap: 12 }}>
    <div style={{
      width: 38, height: 38, borderRadius: 8,
      background: `${color}15`, color,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      {icon}
    </div>
    <div>
      <div style={{ fontSize: 20, fontWeight: 700, lineHeight: 1.1 }}>{value}</div>
      <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 2 }}>{label}</div>
    </div>
  </div>
);

const StatBox: React.FC<{ label: string; value: string | number }> = ({ label, value }) => (
  <div style={{ textAlign: 'center', padding: '12px 8px', background: 'var(--bg-secondary)', borderRadius: 8 }}>
    <div style={{ fontSize: 18, fontWeight: 700 }}>{value}</div>
    <div style={{ fontSize: 10, color: 'var(--text-secondary)', marginTop: 4, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</div>
  </div>
);

const RewardPreview: React.FC<{ title: string; config: RewardConfig }> = ({ title, config }) => (
  <div style={{ padding: '16px 18px', background: 'var(--bg-secondary)', borderRadius: 10, border: '1px solid var(--border)' }}>
    <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{title}</div>
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {config.type === 'premium_extension' && config.days > 0 && (
        <span style={{ fontSize: 14, fontWeight: 600 }}>⭐ {config.days} Tage Premium</span>
      )}
      {config.type === 'discount' && config.discount_percent > 0 && (
        <span style={{ fontSize: 14, fontWeight: 600 }}>💰 {config.discount_percent}% Rabatt</span>
      )}
      {(config.type === 'combined' || config.bonus_xp > 0) && config.bonus_xp > 0 && (
        <span style={{ fontSize: 14, fontWeight: 600 }}>⚡ {config.bonus_xp} Bonus-XP</span>
      )}
      {config.type === 'combined' && config.days > 0 && (
        <span style={{ fontSize: 14, fontWeight: 600 }}>⭐ {config.days} Tage Premium</span>
      )}
      {config.type === 'combined' && config.discount_percent > 0 && (
        <span style={{ fontSize: 14, fontWeight: 600 }}>💰 {config.discount_percent}% Rabatt</span>
      )}
    </div>
  </div>
);

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '10px 14px',
  borderRadius: 10,
  border: '1.5px solid var(--border)',
  background: 'var(--bg-primary)',
  fontSize: 14,
  fontWeight: 500,
  outline: 'none',
  transition: 'border-color 0.2s ease',
  boxSizing: 'border-box',
  color: 'var(--text-primary)',
};

const selectStyle: React.CSSProperties = {
  ...inputStyle,
  cursor: 'pointer',
  appearance: 'auto',
};

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: 13,
  fontWeight: 600,
  marginBottom: 8,
  color: 'var(--text-secondary)',
};

const fieldGroupStyle: React.CSSProperties = {
  marginBottom: 0,
};

const RewardEditor: React.FC<{ config: RewardConfig; onChange: (c: RewardConfig) => void }> = ({ config, onChange }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
    {/* Reward Type Selector */}
    <div style={fieldGroupStyle}>
      <label style={labelStyle}>Belohnungstyp</label>
      <select
        value={config.type}
        onChange={e => onChange({ ...config, type: e.target.value })}
        style={selectStyle}
      >
        {Object.entries(REWARD_TYPES).map(([k, v]) => (
          <option key={k} value={k}>{v.emoji} {v.label}</option>
        ))}
      </select>
      <p style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 8, lineHeight: 1.4 }}>
        {REWARD_TYPES[config.type]?.desc}
      </p>
    </div>

    {/* Conditional fields in a grid */}
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 16 }}>
      {(config.type === 'premium_extension' || config.type === 'combined') && (
        <div style={fieldGroupStyle}>
          <label style={labelStyle}>⭐ Premium-Tage</label>
          <input
            type="number"
            min={0}
            value={config.days}
            onChange={e => onChange({ ...config, days: parseInt(e.target.value) || 0 })}
            style={inputStyle}
            placeholder="z.B. 30"
          />
        </div>
      )}

      {(config.type === 'discount' || config.type === 'combined') && (
        <div style={fieldGroupStyle}>
          <label style={labelStyle}>💰 Rabatt (%)</label>
          <input
            type="number"
            min={0}
            max={100}
            value={config.discount_percent}
            onChange={e => onChange({ ...config, discount_percent: parseInt(e.target.value) || 0 })}
            style={inputStyle}
            placeholder="z.B. 20"
          />
        </div>
      )}

      <div style={fieldGroupStyle}>
        <label style={labelStyle}>⚡ Bonus-XP</label>
        <input
          type="number"
          min={0}
          value={config.bonus_xp}
          onChange={e => onChange({ ...config, bonus_xp: parseInt(e.target.value) || 0 })}
          style={inputStyle}
          placeholder="z.B. 500"
        />
      </div>
    </div>
  </div>
);
